import { useMemo } from "react";
import type { ChartAnnotationPoint, OhlcCandlePoint } from "../types";
import { computeEMAs } from "@/src/utils/technicalAnalysisCalc";
import { cn } from "utils";

interface MiniCandleChartProps {
  candles: OhlcCandlePoint[];
  annotation?: ChartAnnotationPoint;
  className?: string;
}

const EMA7_PERIOD = 7;
const EMA14_PERIOD = 14;

const backfillFinite = (line: number[], fallback: number[]) =>
  line.map((v, i) => (Number.isFinite(v) ? v : fallback[i]!));

export const formatEmaDisplay = (value: number) =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** 由 OHLC 推导 EMA 折线与 Y 轴刻度（柱子像素高度仍由 OHLC 在 scaleY 中换算） */
export const deriveCandleChartSeries = (candles: OhlcCandlePoint[]) => {
  const closes = candles.map((c) => c.close);
  const emas = computeEMAs(closes, [EMA7_PERIOD, EMA14_PERIOD]);
  const ema7Line = backfillFinite(emas[EMA7_PERIOD], closes);
  const ema14Line = backfillFinite(emas[EMA14_PERIOD], closes);

  const lows = candles.map((c) => c.low);
  const highs = candles.map((c) => c.high);
  const min = Math.min(...lows, ...ema7Line, ...ema14Line);
  const max = Math.max(...highs, ...ema7Line, ...ema14Line);
  const mid = (min + max) / 2;
  const toLabel = (v: number) => `${(v / 1000).toFixed(1)}k`;

  return {
    ema7Line,
    ema14Line,
    yAxisLabels: [toLabel(max), toLabel(mid), toLabel(min)],
    lastEma7: ema7Line[ema7Line.length - 1] ?? 0,
    lastEma14: ema14Line[ema14Line.length - 1] ?? 0,
  };
};

/**
 * 蜡烛图 + EMA 双线 + 标注。 Figma：94103:23420
 * 外部仅传 OHLC；实体/影线高度、EMA、Y 轴刻度均由组件内部计算。
 */
export const MiniCandleChart: React.FC<MiniCandleChartProps> = ({
  candles,
  annotation,
  className,
}) => {
  const { ema7Line, ema14Line, yAxisLabels } = useMemo(
    () => deriveCandleChartSeries(candles),
    [candles],
  );

  const width = 280;
  const height = 60;
  const paddingX = 2;
  const paddingY = 4;
  const lows = candles.map((c) => c.low);
  const highs = candles.map((c) => c.high);
  const min = Math.min(...lows, ...ema7Line, ...ema14Line);
  const max = Math.max(...highs, ...ema7Line, ...ema14Line);
  const range = max - min || 1;
  const innerW = width - paddingX * 2;
  const innerH = height - paddingY * 2;
  const barW = candles.length > 0 ? innerW / candles.length : 0;

  const scaleY = (value: number) =>
    paddingY + (1 - (value - min) / range) * innerH;

  const toLine = (values: number[]) =>
    values
      .map((value, index) => {
        const x = paddingX + (index + 0.5) * barW;
        const y = scaleY(value);
        return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");

  const annotationPoint =
    annotation && candles[annotation.pointIndex]
      ? {
          x: paddingX + (annotation.pointIndex + 0.5) * barW,
          y: scaleY(candles[annotation.pointIndex].high),
        }
      : null;

  return (
    <div className={cn("relative flex w-full gap-2", className)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="min-w-0 flex-1"
        aria-hidden
      >
        {candles.map((candle, index) => {
          const x = paddingX + index * barW + barW * 0.25;
          const bodyW = barW * 0.5;
          const isUp = candle.close >= candle.open;
          const highY = scaleY(candle.high);
          const lowY = scaleY(candle.low);
          const openY = scaleY(candle.open);
          const closeY = scaleY(candle.close);
          const bodyTop = Math.min(openY, closeY);
          const bodyHeight = Math.max(1, Math.abs(closeY - openY));
          return (
            <g key={index}>
              <line
                x1={x + bodyW / 2}
                y1={highY}
                x2={x + bodyW / 2}
                y2={lowY}
                className={
                  isUp
                    ? "stroke-content_accent1_primary"
                    : "stroke-content_accent2_primary"
                }
                strokeWidth={1}
              />
              <rect
                x={x}
                y={bodyTop}
                width={bodyW}
                height={bodyHeight}
                className={
                  isUp
                    ? "fill-content_accent1_primary"
                    : "fill-content_accent2_primary"
                }
              />
            </g>
          );
        })}
        <path
          d={toLine(ema7Line)}
          fill="none"
          className="stroke-content_brand_primary"
          strokeWidth={1}
        />
        <path
          d={toLine(ema14Line)}
          fill="none"
          className="stroke-border_accent3_solid"
          strokeWidth={1}
        />
      </svg>
      <div className="flex w-5 shrink-0 flex-col justify-between body_medium_xs text-content_quaternary">
        {yAxisLabels.map((label, index) => (
          <span key={`${label}-${index}`}>{label}</span>
        ))}
      </div>
      {annotation && annotationPoint ? (
        <div
          className="absolute flex flex-col items-center"
          style={{
            left: `calc(${(annotationPoint.x / width) * 100}% - 12px)`,
            top: Math.max(0, annotationPoint.y - 22),
          }}
        >
          <span className="rounded-1 bg-content_accent2_primary px-1 body_medium_xs text-content_inverse_primary">
            {annotation.label}
          </span>
          <div className="size-0 border-x-4 border-x-transparent border-t-4 border-t-content_accent2_primary" />
        </div>
      ) : null}
    </div>
  );
};
